import { Col, Divider, Row } from 'antd';
import React, { FC, useContext, useEffect } from 'react';
import FieldsRow from 'shared/components/FieldsRow';
import styles from './category5.module.scss';
import {
  Control,
  FieldValues,
  useFieldArray,
  useWatch,
  UseFormTrigger,
} from 'react-hook-form';
import {
  ScopeThreeData,
  ScopeWasteType,
} from 'models/ActivityDataEntryScopes/scopeThree.model';
import { getWasteTypeFieldsData, getWeightsData } from './category5LayoutData';
import { queryParamsList } from 'views/ActivityDataEntry';
import { HelpDeskTooltipContext } from 'context/HelpDeskTooltipContext';
import { HelpDeskInputFieldEnum } from 'context/HelpDeskTooltipContext/helpDeskInputFieldEnum';
import ActivityDataEntryService from 'services/ActivityDataEntryService';
import { ReactComponent as AddIcon } from 'assets/icons/add_enclosed.svg';
import useFilters from 'shared/hooks/useFilters';
import { useScopeThreeFormEdit } from 'context/ScopeThreeFormEditContext';
import WasteDiversionRateCard from './WasteDiversionRateCard';

interface Category5LayoutProps {
  control: Control<ScopeThreeData>;
  trigger?: UseFormTrigger<FieldValues>;
}

const Category5Layout: FC<Category5LayoutProps> = (
  props: Category5LayoutProps
) => {
  const { control, trigger } = props;
  const { isEditView } = useScopeThreeFormEdit();
  const readOnly = !isEditView;

  const { openTooltipModal } = useContext(HelpDeskTooltipContext);
  const { fetchWasteTypeMetaData, wasteTypeListMeta } =
    ActivityDataEntryService();
  const { getParams } = useFilters();

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'scopeWasteType',
  });
  const wasteDiversionRate = useWatch({
    control,
    name: 'wasteDiversionRate',
  });
  const { isProcessingFacility: isPF } = getParams(queryParamsList);
  const isProcessingFacility = isPF !== 'true';

  useEffect(
    () => {
      fetchWasteTypeMetaData();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- waste-type meta bootstrap
    []
  );

  const appendWasteType = () => {
    if (readOnly) return;
    append({ ...new ScopeWasteType() });
  };

  const handleToolTipOpen = (fieldName: HelpDeskInputFieldEnum) => () =>
    openTooltipModal(fieldName);

  const handleChange = async () => await trigger?.();

  const weightFieldsData = getWeightsData({
    handleToolTipOpen,
    isProcessingFacility,
    handleChange,
    readOnly,
  });
  return (
    <Row>
      <Col span={22}>
        <Row>
          <Col span={22}>
            <WasteDiversionRateCard rate={wasteDiversionRate} />
          </Col>
          <Col span={22}>
            <Divider />
          </Col>
          <Col span={24}>
            <FieldsRow {...weightFieldsData} />
          </Col>
          <Col span={22}>
            <Divider />
          </Col>
          <Col span={22} className={styles.wasteTypeHeader}>
            <Row align="middle">
              <Col className={styles.wasteTypeTitle} span={24}>
                Waste Type
              </Col>
              <Col span={24}>
                {!readOnly && (
                  <Row align="middle" justify="end">
                    <div
                      className={styles.addWasteType}
                      onClick={appendWasteType}>
                      <Col className={styles.addIcon}>
                        <AddIcon />
                      </Col>
                      <Col>Waste type</Col>
                    </div>
                  </Row>
                )}
              </Col>
            </Row>
          </Col>
          {fields?.map((field, index) => (
            <Col span={24} className={styles.fieldsRow} key={field?.id}>
              <FieldsRow
                {...getWasteTypeFieldsData({
                  index,
                  handleToolTipOpen,
                  remove,
                  arrayLength: fields?.length,
                  wasteTypeListMeta,
                  handleChange,
                  readOnly,
                })}
              />
            </Col>
          ))}
        </Row>
      </Col>
    </Row>
  );
};

export default Category5Layout;
